import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  NewRankV2List,
  NewRankV2RankList,
  NewUserActivity,
  TournamentList,
  TournamentRankList,
  WheelCondition,
  WheelHistory,
  WheelInfo,
  WheelPrize,
} from '../interfaces/activity.interface';
import { ResponseData } from '../interfaces/response.interface';
import { BaseApi } from './base.api';

@Injectable({
  providedIn: 'root',
})
export class ActivityApi extends BaseApi {
  /**
   * 转动轮盘
   *
   * @param activityCode 活动id
   * @returns
   */
  turntablerun(activityCode: string): Observable<ResponseData<WheelPrize>> {
    const url = `${environment.apiUrl}/v1/resource/activity/turntablerun`;
    return this.post(url, { activityCode });
  }

  /**
   * 获取活动资讯
   *
   * @returns
   */
  getturntableinformation(): Observable<ResponseData<WheelInfo[]>> {
    const url = `${environment.apiUrl}/v1/resource/activity/getmoreturntableinformation`;
    return this.get(url);
  }

  /**
   * 取得剩余次数、距离下次抽奖的条件
   *
   * @param activityCode 活动id
   * @returns
   */
  getturntablegaptospin(activityCode: string): Observable<ResponseData<WheelCondition>> {
    const url = `${environment.apiUrl}/v1/resource/activity/getturntablegaptospin`;
    return this.get(url, { activityCode });
  }

  /**
   * 获取大转盘中奖历史（所有人）
   *
   * @param startTime 开始时间
   * @param endTime 结束时间
   * @param pageIndex 当前页码
   * @param pageSize 每页条数
   * @returns
   */
  getturntableprizehistory(
    startTime: number,
    endTime: number,
    pageIndex: number,
    pageSize: number,
  ): Observable<ResponseData<WheelHistory>> {
    const url = `${environment.apiUrl}/v1/resource/activity/getturntableprizehistory`;
    return this.get(url, { startTime, endTime, pageIndex, pageSize });
  }

  /**
   * 获取大转盘转动历史（用户自己）
   *
   * @param startTime 开始时间
   * @param endTime 结束时间
   * @param pageIndex 当前页码
   * @param pageSize 每页条数
   * @returns
   */
  getturntablspinhistory(
    startTime: number,
    endTime: number,
    pageIndex: number,
    pageSize: number,
  ): Observable<ResponseData<WheelHistory>> {
    const url = `${environment.apiUrl}/v1/resource/activity/getturntablspinhistory`;
    return this.get(url, { startTime, endTime, pageIndex, pageSize });
  }

  /**
   * 查询用户可申请的新用户红利
   *
   * @returns
   */
  getnewuseractivityapply(): Observable<ResponseData<NewUserActivity[]>> {
    const url = `${environment.apiUrl}/v1/resource/activity/getnewuseractivityapply`;
    return this.get(url);
  }

  /**
   * 用户申请新用户红利
   *
   * @param prizeCode 奖品编号
   * @param tmpCode 活动编号
   * @param tmpType 模板类型 rank-竞赛模板 newuser-新人模板 deposit-首存送 guess-竞猜 spin-轮盘
   * @returns
   */
  newuseractivityapply(prizeCode: string, tmpCode: string, tmpType: string): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/resource/activity/newuseractivityapply`;
    return this.post(url, { prizeCode, tmpCode, tmpType });
  }

  /**
   * 获取 tournament 列表
   *
   * @param params
   * @param params.startDto
   * @param params.startDto.current
   * @param params.startDto.orderDirection
   * @param params.startDto.size
   * @param params.endDto
   * @param params.endDto.current
   * @param params.endDto.orderDirection
   * @param params.endDto.size
   * @param params.preDto
   * @param params.preDto.current
   * @param params.preDto.orderDirection
   * @param params.preDto.size
   * @returns
   */
  getTournamentList(params: {
    startDto: {
      current: number;
      orderDirection: 'desc' | 'asc';
      size: number;
    };
    endDto: {
      current: number;
      orderDirection: 'desc' | 'asc';
      size: number;
    };
    preDto: {
      current: number;
      orderDirection: 'desc' | 'asc';
      size: number;
    };
  }): Observable<TournamentList> {
    const url = `${environment.apiUrl}/v1/resource/activity/getnewranklist`;
    return this.post<ResponseData<TournamentList>>(url, params).pipe(
      map(
        v =>
          v?.data || {
            endList: null,
            startList: null,
            perList: null,
          },
      ),
    );
  }

  /**
   * tournament 报名
   *
   * @param params
   * @param params.tmpCode
   * @returns
   */
  onTournamentApply(params: { tmpCode: string }): Observable<boolean> {
    const url = `${environment.apiUrl}/v1/resource/activity/newrankactivityapply`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(v => v?.data || false));
  }

  /**
   * 获取 tournament 排名
   *
   * @param params
   * @param params.gameTypeDto 活动内页传
   * @param params.gameTypeDto.gameCategory
   * @param params.gameTypeDto.gameCode
   * @param params.gameTypeDto.gameProvider
   * @param params.tmpCodes 竞赛活动主页传
   * @param params.current
   * @param params.size
   * @returns
   */
  tournamentRankList(params: {
    gameTypeDto?: {
      gameCategory: string;
      gameCode: string;
      gameProvider: string;
    };
    tmpCodes?: string[];
    current?: number;
    size?: number;
  }): Observable<TournamentRankList[]> {
    const url = `${environment.apiUrl}/v1/resource/activity/getnewrankresult`;

    return this.post<ResponseData<TournamentRankList[]>>(url, params).pipe(map(v => v?.data || []));
  }

  /**
   * tournament 详情页面 是否可以报名
   *
   * @param params
   * @param params.tmpCodes
   * @returns
   */
  onNewRankCheckApply(params: { tmpCodes: string[] }): Observable<{ canJoin: boolean; tmpCode: string }[]> {
    const url = `${environment.apiUrl}/v1/resource/activity/newrankcheckapply`;

    return this.post<ResponseData<{ canJoin: boolean; tmpCode: string }[]>>(url, params).pipe(map(v => v?.data || []));
  }

  /**
   * 取得新競賽活動列表 (V2)
   *
   * @param params
   * @param params.startDto 進行中活動分頁參數
   * @param params.startDto.current
   * @param params.startDto.orderDirection
   * @param params.startDto.size
   * @param params.endDto 已結束活動分頁參數
   * @param params.endDto.current
   * @param params.endDto.orderDirection
   * @param params.endDto.size
   * @param params.preDto 未開始活動分頁參數
   * @param params.preDto.current
   * @param params.preDto.orderDirection
   * @param params.preDto.size
   * @returns
   */
  getNewRankV2List(params: {
    startDto: {
      current: number;
      orderDirection: 'desc' | 'asc';
      size: number;
    };
    endDto: {
      current: number;
      orderDirection: 'desc' | 'asc';
      size: number;
    };
    preDto: {
      current: number;
      orderDirection: 'desc' | 'asc';
      size: number;
    };
  }): Observable<NewRankV2List> {
    const url = `${environment.apiUrl}/v1/resource/activity/GetNewRankV2List`;
    return this.post<ResponseData<NewRankV2List>>(url, params).pipe(
      map(v => v?.data || { startList: null, endList: null, perList: null }),
    );
  }

  /**
   * 取得競賽排名 (V2)
   *
   * @param params
   * @param params.tmpCodes 活動代碼陣列
   * @param params.current 排名頁碼，從 1 開始
   * @param params.size 每頁筆數
   * @param params.gameTypeDto 指定遊戲類型篩選，不篩則傳 null
   * @returns
   */
  getNewRankV2Result(params: {
    tmpCodes: string[];
    current: number;
    size: number;
    gameTypeDto: {
      gameCategory: string;
      gameCode: string;
      gameProvider: string;
    } | null;
  }): Observable<NewRankV2RankList[]> {
    const url = `${environment.apiUrl}/v1/resource/activity/GetNewRankV2Result`;
    return this.post<ResponseData<NewRankV2RankList[]>>(url, params).pipe(map(v => v?.data || []));
  }
}
